/**
 * Body editor for the weighting step: apply-vs-calculate mode. Apply mode
 * is a single weights-field slot; calculate mode is the destination field,
 * one or more rim dimensions (container slot + auto-populated category
 * targets), an optional subgroup filter, and the normalization controls.
 */
import React, { useCallback } from "react"
import { Button, Radio, Tooltip } from "antd"
import type { RadioChangeEvent } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowsRotate,
  faCircleInfo,
  faPlus,
  faXmark,
} from "@fortawesome/free-solid-svg-icons"
import { QueryBuilderStore } from "stores/queryBuilderStore"
import { useWeightingReportStore } from "stores/weightingReportStore"
import { WeightingStep } from "types/queryBuilder.types"
import { CodebookData } from "types/codebook.types"
import { getBoolChildLabels } from "utils/codebookUtils"
import { reportForWeights } from "features/QueryConsole/components/WeightingReport/weightingReportRows"
import WeightingReportView from "features/QueryConsole/components/WeightingReport/WeightingReport.component"
import SingleFieldSlot from "../SingleFieldSlot.component"
import { StepBodyProps } from "./stepBodyProps"

export interface WeightingStepBodyProps extends StepBodyProps<WeightingStep> {
  setWeightingMode: QueryBuilderStore["setWeightingMode"]
  setWeightingField: QueryBuilderStore["setWeightingField"]
  setWeightingDest: QueryBuilderStore["setWeightingDest"]
  addWeightingDimension: QueryBuilderStore["addWeightingDimension"]
  removeWeightingDimension: QueryBuilderStore["removeWeightingDimension"]
  setWeightingDimensionContainer: QueryBuilderStore["setWeightingDimensionContainer"]
  setWeightingDimensionTargets: QueryBuilderStore["setWeightingDimensionTargets"]
  setWeightingDimensionTargetValue: QueryBuilderStore["setWeightingDimensionTargetValue"]
  setWeightingFilter: QueryBuilderStore["setWeightingFilter"]
  setWeightingNormalize: QueryBuilderStore["setWeightingNormalize"]
  setWeightingNormalizeValue: QueryBuilderStore["setWeightingNormalizeValue"]
  /** Raw codebook tree — used to read a dropped container's BOOL children. */
  codebook: CodebookData | undefined
}

const WeightingStepBody: React.FC<WeightingStepBodyProps> = ({
  step,
  setWeightingMode,
  setWeightingField,
  setWeightingDest,
  addWeightingDimension,
  removeWeightingDimension,
  setWeightingDimensionContainer,
  setWeightingDimensionTargets,
  setWeightingDimensionTargetValue,
  setWeightingFilter,
  setWeightingNormalize,
  setWeightingNormalizeValue,
  codebook,
}) => {
  // A dropped container's BOOL children become that variable's categories,
  // preserving any target %s already entered.
  const populateWeightingDimension = useCallback(
    (dimIndex: number, container: string): void => {
      if (step.type !== "weighting") return
      const labels = getBoolChildLabels(codebook, container)
      const existing = new Map(
        (step.dimensions[dimIndex]?.targets ?? []).map((t) => [
          t.label,
          t.target,
        ])
      )
      setWeightingDimensionTargets(
        step.id,
        dimIndex,
        labels.map((label) => ({ label, target: existing.get(label) ?? "" }))
      )
    },
    [codebook, step, setWeightingDimensionTargets]
  )

  // What the last Apply's CALCWEIGHTS achieved for this step's weight field.
  // reportForWeights returns an element of the stored array, so the selector
  // result is a stable reference between renders.
  const report = useWeightingReportStore((s) =>
    step.mode === "calculate" ? reportForWeights(s.reports, step.destField) : undefined
  )

  return (
    <>
      <div className="qc-step__field-row">
        <span className="qc-step__field-label">Mode:</span>
        <Radio.Group
          value={step.mode}
          onChange={(e: RadioChangeEvent) =>
            setWeightingMode(step.id, e.target.value)
          }
          size="small"
        >
          <Radio.Button value="apply">Apply existing</Radio.Button>
          <Radio.Button value="calculate">Calculate</Radio.Button>
        </Radio.Group>
      </div>

      {step.mode === "apply" ? (
        <div className="qc-step__field-row">
          <span className="qc-step__field-label">
            Weights field:
            <Tooltip
              title="A REAL column of per-respondent weights to activate for every following table (USEWEIGHTS)."
              placement="top"
              overlayClassName="dataq-tooltip"
            >
              <FontAwesomeIcon
                icon={faCircleInfo}
                className="qc-step__field-info"
              />
            </Tooltip>
          </span>
          <SingleFieldSlot
            value={step.weightsField}
            onChange={(v) => setWeightingField(step.id, v)}
            placeholder="Drag the existing weights column"
          />
        </div>
      ) : (
        <>
          <div className="qc-step__field-row">
            <span className="qc-step__field-label">
              Weights field name:
              <Tooltip
                title="The REAL field the computed weights are written to (declared and seeded to 1.0, then applied automatically)."
                placement="top"
                overlayClassName="dataq-tooltip"
              >
                <FontAwesomeIcon
                  icon={faCircleInfo}
                  className="qc-step__field-info"
                />
              </Tooltip>
            </span>
            <input
              className="qc-recodes__name qc-mono"
              type="text"
              value={step.destField}
              onChange={(e) =>
                setWeightingDest(step.id, e.target.value)
              }
              placeholder="weights"
              aria-label="Weights field name"
            />
          </div>
          <div className="qc-step__field-label">
            Weight to targets
            <Tooltip
              title="Add one or more weighting variables (e.g. Gender, Age, Region). Each is a container of yes/no categories: drag it in and its categories auto-populate. Set a target % for each (per variable they add to 100, or leave some blank and those share what's left). All variables are weighted together in one command. After Apply, this step shows each target against what the weights reached."
              placement="top"
              overlayClassName="dataq-tooltip"
            >
              <FontAwesomeIcon
                icon={faCircleInfo}
                className="qc-step__field-info"
              />
            </Tooltip>
          </div>
          <div className="qc-weight__dims">
            {step.dimensions.map((dim, di) => (
              <div className="qc-weight__dim" key={di}>
                <div className="qc-step__field-row">
                  <span className="qc-step__field-label">
                    Variable {di + 1}:
                  </span>
                  <SingleFieldSlot
                    value={dim.container}
                    onChange={(container) => {
                      setWeightingDimensionContainer(
                        step.id,
                        di,
                        container
                      )
                      populateWeightingDimension(di, container)
                    }}
                    placeholder="Drag a weighting variable (group of yes/no categories)"
                  />
                  {dim.container.trim() && (
                    <Tooltip title="Re-read categories from the variable">
                      <button
                        type="button"
                        className="qc-cat__refresh"
                        aria-label="Refresh categories"
                        onClick={() =>
                          populateWeightingDimension(
                            di,
                            dim.container
                          )
                        }
                      >
                        <FontAwesomeIcon icon={faArrowsRotate} />
                      </button>
                    </Tooltip>
                  )}
                  {step.dimensions.length > 1 && (
                    <button
                      type="button"
                      className="qc-recodes__remove"
                      aria-label="Remove this variable"
                      onClick={() =>
                        removeWeightingDimension(step.id, di)
                      }
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  )}
                </div>
                <div className="qc-cat__categories">
                  {dim.targets.length === 0 ? (
                    <p className="qc-cat__empty">
                      {dim.container.trim()
                        ? "No yes/no categories found for this variable in the data tree yet."
                        : "Drag a variable above to list its categories."}
                    </p>
                  ) : (
                    dim.targets.map((t, ti) => (
                      <div
                        className="qc-cat__row"
                        key={t.label || ti}
                      >
                        <span
                          className="qc-cat__label qc-mono"
                          title={t.label}
                        >
                          {t.label}
                        </span>
                        <span className="qc-cat__arrow" aria-hidden>
                          →
                        </span>
                        <input
                          className="qc-recodes__name qc-mono qc-cat__range"
                          type="text"
                          value={t.target}
                          onChange={(e) =>
                            setWeightingDimensionTargetValue(
                              step.id,
                              di,
                              ti,
                              e.target.value
                            )
                          }
                          placeholder="target % (e.g. 50)"
                          aria-label={`Target percent for ${t.label}`}
                        />
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))}
            <Button
              type="dashed"
              className="self-start"
              icon={<FontAwesomeIcon icon={faPlus} />}
              onClick={() => addWeightingDimension(step.id)}
            >
              Add weighting variable
            </Button>
          </div>
          <div className="qc-step__field-row">
            <span className="qc-step__field-label">
              Only respondents where (optional):
              <Tooltip
                title="Restrict the weighting to a subgroup — the weights are computed only over respondents where this yes/no field is true (e.g. completed interviews, or the main sample excluding a booster). Everyone else keeps weight 1.0. Leave blank to weight everyone."
                placement="top"
                overlayClassName="dataq-tooltip"
              >
                <FontAwesomeIcon
                  icon={faCircleInfo}
                  className="qc-step__field-info"
                />
              </Tooltip>
            </span>
            <SingleFieldSlot
              value={step.filter}
              onChange={(v) => setWeightingFilter(step.id, v)}
              placeholder="Drag a yes/no field to weight a subgroup only"
            />
          </div>
          <div className="qc-step__field-row">
            <span className="qc-step__field-label">Scale to:</span>
            <Radio.Group
              value={step.normalize}
              onChange={(e: RadioChangeEvent) =>
                setWeightingNormalize(step.id, e.target.value)
              }
              size="small"
            >
              <Radio.Button value="none">none</Radio.Button>
              <Radio.Button value="average">average</Radio.Button>
              <Radio.Button value="sum">sum</Radio.Button>
            </Radio.Group>
            {step.normalize !== "none" && (
              <input
                className="qc-recodes__name qc-mono qc-cat__range"
                type="text"
                value={step.normalizeValue}
                onChange={(e) =>
                  setWeightingNormalizeValue(step.id, e.target.value)
                }
                placeholder={
                  step.normalize === "average"
                    ? "average weight (e.g. 1.0)"
                    : "total of weights (e.g. 1000)"
                }
                aria-label="Normalization value"
              />
            )}
          </div>
          {report && (
            <div className="mt-3 border-t pt-2">
              <p className="m-0 text-xs text-gray-500">Result of the last Apply</p>
              <WeightingReportView report={report} />
            </div>
          )}
        </>
      )}
    </>
  )
}

export default WeightingStepBody
